import axios from 'axios';
import db from './db';

// const API_URL = 'http://localhost:5000/api';
// const API_URL = `http://${window.location.hostname}:5000/api`;
// const api = axios.create({
//   baseURL: API_URL,
//   headers: { 'Content-Type': 'application/json' },
// });


const API_URL = 'https://nfcfied-access-backend.onrender.com/api';
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});


// Request interceptor for token
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor – only redirect if online and 401
// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't process requests explicitly excluded from offline handling
    if (error.config?._skipOfflineQueue) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const requestUrl = error.config?.url;

    // 401 from LOGIN means invalid credentials.
    // Let Login.jsx handle this error.
    const isLoginRequest = requestUrl?.includes('/auth/login');

    if (
      status === 401 &&
      navigator.onLine &&
      !isLoginRequest
    ) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');

      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// ---------- Offline Helpers ----------
const isOnline = () => navigator.onLine;

const queueOperation = async (
  endpoint,
  method,
  body
) => {
  return db.syncQueue.add({
    endpoint,
    method,
    body,
    timestamp: Date.now(),
    retryCount: 0
  });
};

export const syncPendingOperations = async () => {
  if (!isOnline()) return;
  const pending = await db.syncQueue.toArray();
  for (const op of pending) {
    try {
      let requestBody = op.body;
      // Convert date strings for event creation/update
      if (op.endpoint === '/events' && op.method === 'POST') {
        requestBody = { ...op.body };
        if (requestBody.startDate) requestBody.startDate = new Date(requestBody.startDate);
        if (requestBody.endDate) requestBody.endDate = new Date(requestBody.endDate);
      } else if (op.endpoint.startsWith('/events/') && op.method === 'PUT') {
        requestBody = { ...op.body };
        if (requestBody.startDate) requestBody.startDate = new Date(requestBody.startDate);
        if (requestBody.endDate) requestBody.endDate = new Date(requestBody.endDate);
      }
      await api({ method: op.method, url: op.endpoint, data: requestBody });
      await db.syncQueue.delete(op.id);
    } catch (err) {
      console.error(`Sync failed for ${op.endpoint}:`, err);
      await db.syncQueue.update(op.id, { retryCount: op.retryCount + 1 });
    }
  }
  // await ;
};

export const refreshAllCachedData = async () => {
  console.log('🔥 CACHE REFRESH STARTED');

  if (!isOnline()) {
    console.log('❌ OFFLINE - CACHE REFRESH SKIPPED');
    return;
  }

  const token = sessionStorage.getItem('token');

  if (!token) {
    console.log('❌ NO TOKEN - CACHE REFRESH SKIPPED');
    return;
  }

  try {
    // =====================================================
    // Fetch only the first page of each resource
    // =====================================================

    const [
      eventsRes,
      devicesRes,
      usersRes,
    ] = await Promise.all([
      getAllEvents(1, 20),
      getDevices(1, 50),
      getUsers(1, 50),
    ]);

    // =====================================================
    // Normalize API responses
    // =====================================================

    const events =
      eventsRes?.data?.data ||
      eventsRes?.data ||
      [];

    const devices =
      devicesRes?.data?.data ||
      devicesRes?.data ||
      [];

    const users =
      usersRes?.data?.data ||
      usersRes?.data ||
      [];

    // =====================================================
    // Update Events cache
    // =====================================================

    if (events.length > 0) {
      await db.events.bulkPut(
        events.map(event => ({
          ...event,
          synced: true,
        }))
      );
    }

    // =====================================================
    // Update Devices cache
    // =====================================================

    if (devices.length > 0) {
      await db.devices.bulkPut(
        devices.map(device => ({
          ...device,
          synced: true,
        }))
      );
    }

    // =====================================================
    // Update Users cache
    // =====================================================

    if (users.length > 0) {
      await db.users.bulkPut(
        users.map(user => ({
          ...user,
          synced: true,
        }))
      );
    }

    console.log('✅ CACHE REFRESH COMPLETE');

    console.log(
      `Events: ${events.length}, ` +
      `Devices: ${devices.length}, ` +
      `Users: ${users.length}`
    );

  } catch (error) {
    console.error(
      '❌ CACHE REFRESH FAILED:',
      error
    );
  }
};
// Helper to enrich offline sessions with names from cached events, devices, users
async function getOfflineSessionsWithNames() {
  const sessions = await db.sessions.toArray();
  const events = await db.events.toArray(); // each event contains its users array
  const devices = await db.devices.toArray();
  const enriched = sessions.map(s => {
    const event = events.find(e => e._id === s.eventId);
    const user = event?.users?.find(u => u.customId === s.customId);
    const entryDevice = devices.find(d => d._id === s.entryDevice);
    const exitDevice = devices.find(d => d._id === s.exitDevice);
    return {
      ...s,
      customId: s.customId || user?.customId || 'N/A',
      name: s.name || user?.name || 'Unknown',
      email: s.email || user?.email || 'N/A',
      eventId: event || { name: 'Unknown' },
      entryDevice: entryDevice || { name: 'Unknown' },
      exitDevice: exitDevice || null
    };
  });
  return { data: enriched };
}

// ---------- Auth API ----------
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (userData) => api.post('/auth/register', userData);
export const approveUser = (userId) => api.put(`/auth/approve/${userId}`);

// ---------- Users API (global) – now uses /auth/users ----------
export const getUsers = (page = 1, limit = 50, search = '') => 
  api.get('/auth/users', { params: { page, limit, search } });

// Note: createUser, updateUser, deleteUser are not used with /users – use /auth/register instead
// Keeping them for backward compatibility, but they may fail.
export const createUser = async (userData) => {
  if (isOnline()) {
    try {
      const res = await api.post('/auth/register', userData);
      
      return res;
    } catch (err) {
      queueOperation('/auth/register', 'POST', userData);
      throw err;
    }
  } else {
    queueOperation('/auth/register', 'POST', userData);
    return { data: { message: 'User created locally (will sync when online)' } };
  }
};

export const updateUser = async (id, userData) => {
  if (isOnline()) {
    try {
      const res = await api.put(`/auth/users/${id}`, userData);
      
      return res;
    } catch (err) {
      queueOperation(`/auth/users/${id}`, 'PUT', userData);
      throw err;
    }
  } else {
    queueOperation(`/auth/users/${id}`, 'PUT', userData);
    return { data: { message: 'User updated locally' } };
  }
};

export const deleteUser = async (id) => {
  if (isOnline()) {
    try {
      const res = await api.delete(`/auth/users/${id}`);
     
      return res;
    } catch (err) {
      queueOperation(`/auth/users/${id}`, 'DELETE', null);
      throw err;
    }
  } else {
    queueOperation(`/auth/users/${id}`, 'DELETE', null);
    return { data: { message: 'User deleted locally' } };
  }
};

// ---------- Event Managers API – DEPRECATED (use unified users) ----------
// Kept only to avoid breaking imports, but not used in new code.
export const getEventManagers = async () => {
  // Fallback to users with role event_manager
  if (isOnline()) {
    try {
      const res = await api.get('/auth/users');
      const eventManagers = res.data.filter(u => u.role === 'event_manager' || u.role === 'event_editor');
      return { data: eventManagers };
    } catch {
      return { data: [] };
    }
  } else {
    return { data: [] };
  }
};
export const createEventManager = (managerData) => api.post('/auth/register', { ...managerData, role: 'event_manager' });
export const updateEventManager = (id, managerData) => api.put(`/auth/users/${id}`, managerData);
export const deleteEventManager = (id) => api.delete(`/auth/users/${id}`);
export const addEventToManager = (managerId, eventData) => api.post(`/events/assign-to-user`, { userId: managerId, eventIds: [eventData] });
export const recordScan = (managerId, eventId) => api.post(`/sessions/scan`, { eventId, managerId }); // Not correct

// ---------- Admins API – DEPRECATED (use unified users) ----------
export const getAdmins = async () => {
  if (isOnline()) {
    try {
      const res = await api.get('/auth/users');
      const admins = res.data.filter(u => u.role === 'admin' || u.role === 'super_admin');
      return { data: admins };
    } catch {
      return { data: [] };
    }
  } else {
    return { data: [] };
  }
};
export const createAdmin = (adminData) => api.post('/auth/register', { ...adminData, role: 'admin' });
export const updateAdmin = (id, adminData) => api.put(`/auth/users/${id}`, adminData);
export const deleteAdmin = (id) => api.delete(`/auth/users/${id}`);
export const getAdminMyEvents = () => api.get('/events/my-events');

// ---------- Dashboard API ----------
export const getDashboardStats = (startDate, endDate) => {
  let url = '/dashboard/stats';
  if (startDate && endDate) url += `?startDate=${startDate}&endDate=${endDate}`;
  return api.get(url);
};

// ---------- Events API ----------
export const getAllEvents = (page = 1, limit = 20) =>
  api.get('/events', {
    params: {
      page,
      limit,
    },
  });

export const createEvent = async (eventData) => {
  // ==================================================
  // ONLINE
  // ==================================================

  if (isOnline()) {
    try {
      // Server is the source of truth
      const res = await api.post(
        '/events',
        eventData
      );

      // Update local cache separately.
      // A cache failure must NOT turn a successful
      // server creation into a failed mutation.
      try {
        await db.events.put(res.data);
      } catch (cacheError) {
        console.warn(
          'Event created on server, but local cache update failed:',
          cacheError
        );
      }

      return res;

    } catch (error) {

      // ONLY queue when the actual API request failed.
      console.error(
        'Create event API request failed:',
        error
      );

      if (
        !error.response ||
        error.code === 'ERR_NETWORK'
      ) {
        await queueOperation(
          '/events',
          'POST',
          eventData
        );

        try {
          await db.events.put({
            ...eventData,
            _id: `temp_${Date.now()}`,
            synced: false
          });
        } catch (cacheError) {
          console.warn(
            'Failed to cache offline event:',
            cacheError
          );
        }
      }

      throw error;
    }
  }

  // ==================================================
  // OFFLINE
  // ==================================================

  await queueOperation(
    '/events',
    'POST',
    eventData
  );

  try {
    await db.events.put({
      ...eventData,
      _id: `temp_${Date.now()}`,
      synced: false
    });
  } catch (cacheError) {
    console.warn(
      'Failed to cache offline event:',
      cacheError
    );
  }

  return {
    data: {
      message:
        'Event created locally and will sync when online'
    }
  };
};

export const updateEvent = async (eventId, eventData) => {
  if (isOnline()) {
    try {
      const res = await api.put(`/events/${eventId}`, eventData);
      await db.events.update(eventId, { ...res.data, synced: true });
      return res;
    } catch (err) {
      queueOperation(`/events/${eventId}`, 'PUT', eventData);
      await db.events.update(eventId, { ...eventData, synced: false });
      throw err;
    }
  } else {
    queueOperation(`/events/${eventId}`, 'PUT', eventData);
    await db.events.update(eventId, { ...eventData, synced: false });
    return { data: { message: 'Event updated locally' } };
  }
};

export const deleteEvent = async (eventId) => {
  if (isOnline()) {
    try {
      const res = await api.delete(`/events/${eventId}`);
      await db.events.delete(eventId);
      return res;
    } catch (err) {
      queueOperation(`/events/${eventId}`, 'DELETE', null);
      await db.events.delete(eventId);
      throw err;
    }
  } else {
    queueOperation(`/events/${eventId}`, 'DELETE', null);
    await db.events.delete(eventId);
    return { data: { message: 'Event deleted locally' } };
  }
};

export const getEventStats = (eventId) => api.get(`/events/${eventId}/stats`);
export const assignEventsToAdmin = (userId, eventIds) => api.post('/events/assign-to-user', { userId, eventIds });
export const removeAdminFromEvent = (eventId, adminId) => api.delete(`/events/${eventId}/admins/${adminId}`);
export const getEventSessions = (eventId) => api.get(`/events/${eventId}/sessions`);
export const copyUsersToEvent = (targetEventId, sourceEventId) => api.post(`/events/${targetEventId}/copy-users/${sourceEventId}`);
export const getMyEvents = () => api.get('/events/my-events');
export const getVendorEvents = () => api.get('/events/my-events');
// ---------- Event Users API ----------
export const getEventUsers = async (eventId) => {
  if (isOnline()) {
    try {
      const res = await api.get(`/events/${eventId}/users`);
      const event = await db.events.get(eventId);
      if (event) await db.events.update(eventId, { users: res.data });
      return res;
    } catch {
      const event = await db.events.get(eventId);
      return { data: event?.users || [] };
    }
  } else {
    const event = await db.events.get(eventId);
    return { data: event?.users || [] };
  }
};

export const addEventUser = async (eventId, userData) => {
  if (isOnline()) {
    try {
      const res = await api.post(`/events/${eventId}/users`, userData);
      
      return res;
    } catch (err) {
      queueOperation(`/events/${eventId}/users`, 'POST', userData);
      throw err;
    }
  } else {
    queueOperation(`/events/${eventId}/users`, 'POST', userData);
    return { data: { message: 'User added locally (will sync)' } };
  }
};

// In api.js
export const updateEventUser = async (eventId, userId, userData) => {
  if (isOnline()) {
    try {
      // ✅ Use the event-specific endpoint
      const res = await api.put(`/events/${eventId}/users/${userId}`, userData);
      
      return res;
    } catch (err) {
      queueOperation(`/events/${eventId}/users/${userId}`, 'PUT', userData);
      throw err;
    }
  } else {
    queueOperation(`/events/${eventId}/users/${userId}`, 'PUT', userData);
    return { data: { message: 'User updated locally' } };
  }
};

export const deleteEventUser = async (eventId, userId) => {
  if (isOnline()) {
    try {
      const res = await api.delete(`/events/${eventId}/users/${userId}`);
     
      return res;
    } catch (err) {
      queueOperation(`/events/${eventId}/users/${userId}`, 'DELETE', null);
      throw err;
    }
  } else {
    queueOperation(`/events/${eventId}/users/${userId}`, 'DELETE', null);
    return { data: { message: 'User deleted locally' } };
  }
};

// ---------- Devices API ----------
export const getDevices = (page = 1, limit = 50, search = '') => 
  api.get('/devices', { params: { page, limit, search } });

export const createDevice = async (deviceData) => {
  if (isOnline()) {
    try {
      const res = await api.post('/devices', deviceData);
      await db.devices.add(res.data);
      return res;
    } catch (err) {
      queueOperation('/devices', 'POST', deviceData);
      await db.devices.add({ ...deviceData, _id: 'temp_' + Date.now(), synced: false });
      throw err;
    }
  } else {
    queueOperation('/devices', 'POST', deviceData);
    await db.devices.add({ ...deviceData, _id: 'temp_' + Date.now(), synced: false });
    return { data: { message: 'Device created locally' } };
  }
};

export const updateDevice = async (id, deviceData) => {
  if (isOnline()) {
    try {
      const res = await api.put(`/devices/${id}`, deviceData);
      await db.devices.update(id, { ...res.data, synced: true });
      return res;
    } catch (err) {
      queueOperation(`/devices/${id}`, 'PUT', deviceData);
      await db.devices.update(id, { ...deviceData, synced: false });
      throw err;
    }
  } else {
    queueOperation(`/devices/${id}`, 'PUT', deviceData);
    await db.devices.update(id, { ...deviceData, synced: false });
    return { data: { message: 'Device updated locally' } };
  }
};

export const deleteDevice = async (id) => {
  if (isOnline()) {
    try {
      const res = await api.delete(`/devices/${id}`);
      await db.devices.delete(id);
      return res;
    } catch (err) {
      queueOperation(`/devices/${id}`, 'DELETE', null);
      await db.devices.delete(id);
      throw err;
    }
  } else {
    queueOperation(`/devices/${id}`, 'DELETE', null);
    await db.devices.delete(id);
    return { data: { message: 'Device deleted locally' } };
  }
};

// ---------- Session Analytics API ----------

export const getSessionAnalytics = async (eventId) => {
  if (!eventId) {
    throw new Error('eventId is required');
  }

  const response = await api.get('/sessions/analytics', {
    params: {
      eventId,
    },
  });

  return response.data;
};
// ---------- Sessions API ----------
export const getSessions = async ({
  page = 1,
  limit = 50,
  eventId,
  deviceId,
  status,
  startDate,
  endDate,
  search,
  sortOrder = 'desc',
} = {}) => {
  const params = {
    page,
    limit,
  };

  if (eventId) {
    params.eventId = eventId;
  }

  if (deviceId) {
    params.deviceId = deviceId;
  }

  if (status) {
    params.status = status;
  }

  if (startDate) {
    params.startDate = startDate;
  }

  if (endDate) {
    params.endDate = endDate;
  }

  if (search) {
    params.search = search;
  }

  if (sortOrder) {
    params.sortOrder = sortOrder;
  }

  const response = await api.get('/sessions', {
    params,
  });

  return response.data;
};

export const recordSessionScan = async (scanData) => {
  const { customId, eventId, deviceId } = scanData;
  const body = { customId, eventId, deviceId };

  const getExistingSessions = async () => {
    const all = await db.sessions.toArray();
    return all
      .filter((s) => s.customId === customId && s.eventId === eventId)
      .sort((a, b) => new Date(a.entryTime) - new Date(b.entryTime));
  };

  // -------------------------------------------------------------
  // FIX: deviceId from the component is ALREADY the Mongo _id.
  // Do NOT call /devices/deviceId/:id (that route matches the
  // string "deviceId" field, not the ObjectId → 404).
  // -------------------------------------------------------------
  const getDeviceMongoId = async () => {
    // If it's a valid 24-char hex string, it's a Mongo ObjectId
    if (/^[a-f\d]{24}$/i.test(String(deviceId))) {
      return deviceId;
    }
    // Otherwise, look up the cache (offline path)
    const cachedDevices = await db.devices.toArray();
    const device = cachedDevices.find(
      (d) => String(d.deviceId) === String(deviceId),
    );
    return device?._id || deviceId;
  };

  // Get user details from cached users
  let userName = '';
  let userEmail = '';
  const getUserDetails = async () => {
    const cachedUsers = await db.users.toArray();
    const user = cachedUsers.find((u) => u.customId === customId);
    if (user) {
      userName = user.name;
      userEmail = user.email;
    }
  };
  await getUserDetails();

  const existing = await getExistingSessions();
  const lastSession = existing.length > 0 ? existing[existing.length - 1] : null;
  const type = !lastSession || lastSession.status === 'OUT' ? 'ENTRY' : 'EXIT';

  if (isOnline()) {
    try {
      const res = await api.post('/sessions/scan', { ...body, type });

      // ---------------------------------------------------------
      // FIX: MEAL responses have NO `session`. Don't try to cache
      // them into db.sessions — just return the meal payload so the
      // UI can render the mealResults card.
      // ---------------------------------------------------------
      if (res.data?.mode === 'meal') {
        return res;
      }

      // ---------------------------------------------------------
      // ACCESS response — only cache if we actually got a session
      // ---------------------------------------------------------
      const newSession = res.data?.session;
      if (newSession?._id) {
        try {
          await db.sessions.add({ ...newSession, synced: true });
        } catch (cacheErr) {
          console.warn('Failed to cache session locally:', cacheErr);
        }
      }

      window.dispatchEvent(new CustomEvent('sessionScanned'));
      return res;
    } catch (err) {
      // Real backend rejection (400/403/500 with a response body)
      if (err.response) throw err;

      // Network failure — queue and fall through to offline path
      console.error('Network error, queueing:', err);
      await queueOperation('/sessions/scan', 'POST', { ...body, type });
    }
  }

  // ---------------------------------------------------------
  // OFFLINE PATH — ONLY build a session for ACCESS scans.
  // Meal scans can't be redeemed offline (no coupon state),
  // so just return a soft message.
  // ---------------------------------------------------------
  console.log(`Offline: recording ${type} scan locally`);

  await queueOperation('/sessions/scan', 'POST', { ...body, type });

  const devId = await getDeviceMongoId();
  const localSession = {
    _id: 'temp_' + Date.now(),
    customId,
    name: userName,
    email: userEmail,
    eventId,
    entryDevice: devId,
    exitDevice: null,
    status: type === 'ENTRY' ? 'IN' : 'OUT',
    entryTime: new Date(),
    synced: false,
  };

  if (type === 'EXIT') {
    localSession.exitTime = new Date();
    localSession.exitDevice = devId;
  }

  await db.sessions.add(localSession);
  window.dispatchEvent(new CustomEvent('sessionScanned'));

  return {
    data: {
      mode: 'access',
      message: `${type} recorded locally (will sync when online)`,
      session: localSession,
    },
  };
};

export const getSessionStats = () => api.get('/sessions/stats');

// export const getDeviceSessions = async (
//     deviceId
// ) => {
//     let deviceMongoId = deviceId;

//     // =====================================================
//     // 1. Resolve device from local Dexie cache
//     // =====================================================

//     try {
//         const cachedDevices =
//             await db.devices.toArray();

//         const cachedDevice =
//             cachedDevices.find(
//                 device =>
//                     String(device._id) ===
//                         String(deviceId) ||
//                     String(device.deviceId) ===
//                         String(deviceId)
//             );

//         if (cachedDevice) {
//             deviceMongoId =
//                 cachedDevice._id;
//         } else if (isOnline()) {

//             // =================================================
//             // 2. Resolve custom deviceId from backend
//             // =================================================

//             try {
//                 const deviceRes =
//                     await api.get(
//                         `/devices/deviceId/${deviceId}`
//                     );

//                 deviceMongoId =
//                     deviceRes.data._id;

//             } catch (error) {

//                 console.warn(
//                     'Could not resolve deviceId:',
//                     deviceId
//                 );
//             }
//         }

//     } catch (error) {

//         console.warn(
//             'Could not read device cache:',
//             error
//         );
//     }


//     // =====================================================
//     // 3. Online request
//     // =====================================================

//     if (isOnline()) {

//         try {

//             /*
//              * IMPORTANT:
//              *
//              * Do NOT send page/limit here if you
//              * want all sessions.
//              *
//              * Backend should return all sessions.
//              */

//             const res =
//                 await api.get(
//                     `/sessions/device/${deviceMongoId}`
//                 );


//             // =================================================
//             // 4. Get sessions
//             // =================================================

//             let sessions =
//                 Array.isArray(
//                     res.data?.data
//                 )
//                     ? res.data.data
//                     : [];


//             // =================================================
//             // 5. Always sort:
//             //
//             // oldest → newest
//             //
//             // NEW scan = bottom
//             // =================================================

//             sessions.sort(
//                 (a, b) =>
//                     new Date(a.entryTime) -
//                     new Date(b.entryTime)
//             );


//             // =================================================
//             // 6. Pagination information
//             // =================================================

//             const pagination =
//                 res.data?.pagination || {
//                     page: 1,
//                     limit: sessions.length,
//                     total: sessions.length,
//                     totalPages: 1,
//                     hasNextPage: false,
//                     hasPreviousPage: false,
//                 };


//             // =================================================
//             // 7. Normalize sessions for Dexie
//             // =================================================

//             const sessionsToCache =
//                 sessions.map(
//                     session => ({
//                         ...session,

//                         entryDevice:
//                             session
//                                 .entryDevice
//                                 ?._id ||
//                             session.entryDevice,

//                         exitDevice:
//                             session
//                                 .exitDevice
//                                 ?._id ||
//                             session.exitDevice,

//                         synced: true,
//                     })
//                 );


//             // =================================================
//             // 8. Cache sessions
//             // =================================================

//             if (
//                 sessionsToCache.length > 0
//             ) {

//                 await db.sessions.bulkPut(
//                     sessionsToCache
//                 );
//             }


//             // =================================================
//             // 9. Return API response
//             // =================================================

//             return {
//                 ...res,

//                 data: {
//                     data: sessionsToCache,

//                     pagination,
//                 },
//             };


//         } catch (error) {

//             console.warn(
//                 'Online device sessions fetch failed, using cache:',
//                 error
//             );


//             // =================================================
//             // 10. Offline/cache fallback
//             // =================================================

//             const enriched =
//                 await getOfflineSessionsWithNames();


//             let filtered =
//                 enriched.data.filter(
//                     session =>
//                         String(
//                             session
//                                 .entryDevice
//                                 ?._id ||
//                             session.entryDevice
//                         ) ===
//                             String(
//                                 deviceMongoId
//                             ) ||

//                         String(
//                             session
//                                 .exitDevice
//                                 ?._id ||
//                             session.exitDevice
//                         ) ===
//                             String(
//                                 deviceMongoId
//                             )
//                 );


//             // =================================================
//             // 11. Sort offline data
//             //
//             // oldest → newest
//             // =================================================

//             filtered.sort(
//                 (a, b) =>
//                     new Date(a.entryTime) -
//                     new Date(b.entryTime)
//             );


//             // =================================================
//             // 12. Return all cached sessions
//             // =================================================

//             return {
//                 data: {
//                     data: filtered,

//                     pagination: {
//                         page: 1,
//                         limit: filtered.length,
//                         total: filtered.length,
//                         totalPages: 1,
//                         hasNextPage: false,
//                         hasPreviousPage: false,
//                     },
//                 },
//             };
//         }
//     }


//     // =====================================================
//     // 13. Offline mode
//     // =====================================================

//     const enriched =
//         await getOfflineSessionsWithNames();


//     let filtered =
//         enriched.data.filter(
//             session =>
//                 String(
//                     session
//                         .entryDevice
//                         ?._id ||
//                     session.entryDevice
//                 ) ===
//                     String(deviceMongoId) ||

//                 String(
//                     session
//                         .exitDevice
//                         ?._id ||
//                     session.exitDevice
//                 ) ===
//                     String(deviceMongoId)
//         );


//     // =====================================================
//     // 14. Sort offline data
//     //
//     // oldest → newest
//     // =====================================================

//     filtered.sort(
//         (a, b) =>
//             new Date(a.entryTime) -
//             new Date(b.entryTime)
//     );


//     // =====================================================
//     // 15. Return all sessions
//     // =====================================================

//     return {
//         data: {
//             data: filtered,

//             pagination: {
//                 page: 1,
//                 limit: filtered.length,
//                 total: filtered.length,
//                 totalPages: 1,
//                 hasNextPage: false,
//                 hasPreviousPage: false,
//             },
//         },
//     };
// };
export const getDeviceSessions = (deviceId, params) =>
  api.get(`/sessions/device/${deviceId}`, { params });
// ---------- Admin Event Management ----------
export const addUserToMyEvent = (eventId, userId) =>
  api.post(`/events/${eventId}/users`, { userId });
export const removeUserFromMyEvent = (eventId, userId) => api.delete(`/events/my-events/${eventId}/users/${userId}`);

// ---------- Register user (super admin only) ----------
export const registerUser = (userData) => api.post('/auth/register', userData);

// ---------- Assign users to event (super admin) ----------
export const assignUsersToEvent = (eventId, userIds) => api.post(`/events/${eventId}/users`, { userIds });
// Wallet endpoints
export const getWalletBalance = (userId) => api.get(`/wallet/balance/${userId}`);
export const rechargeWallet = (data) => api.post('/wallet/recharge', data);
export const getWalletTransactions = (params) => api.get('/wallet/transactions', { params });
export const getWalletOverview = () => api.get('/wallet/overview');
export const getMyWalletBalance = () => api.get('/wallet/balance/me');
export const getMyWalletTransactions = (params) => api.get('/wallet/transactions/me', { params });
// ============ VENDOR API ============

// Admin: Get all vendors
export const getVendors = () => api.get('/vendors');

// Admin: Get single vendor
export const getVendor = (id) => api.get(`/vendors/${id}`);

// Admin: Create vendor
export const createVendor = (data) => api.post('/vendors', data);

// Admin: Update vendor
export const updateVendor = (id, data) => api.put(`/vendors/${id}`, data);

// Admin: Delete vendor
export const deleteVendor = (id) => api.delete(`/vendors/${id}`);

// Vendor: Get my transactions
export const getVendorTransactions = (params) => api.get('/vendors/me/transactions', { params });

// Vendor: Get my stats
export const getVendorStats = () => api.get('/vendors/me/stats');

// Vendor: Charge attendee
// Payment – charge attendee (now supportsa foodItemId)
export const chargeAttendee = (data) => api.post('/payment/scan', data);

// Food item endpoints
export const getFoodItems = () => api.get('/food');
export const createFoodItem = (data) => api.post('/food', data);
export const updateFoodItem = (id, data) => api.put(`/food/${id}`, data);
export const deleteFoodItem = (id) => api.delete(`/food/${id}`);
export const getFoodItem = (id) => api.get(`/food/${id}`);
export const getOrders = () => api.get('/orders');
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post('/orders', data);
export const payOrder = (id, attendeeId) => {
  console.log('🔵 payOrder called with:', { id, attendeeId });
  // ✅ ADD THESE OPTIONS TO PREVENT THE OFFLINE QUEUE FROM CATCHING IT
  return api.post(`/orders/${id}/pay`, { attendeeId }, {
    _skipOfflineQueue: true // Custom flag
  });
}; export const cancelOrder = (id) => api.delete(`/orders/${id}`);
// Payment – charge attendee (with optional foodItemId)
// export const chargeAttendee = (data) => api.post('/payment/scan', data);
// Add this to your api.js
export const getOrderById = (id) => {
  return api.get(`/orders/${id}`); // Matches your backend GET route
};
// Wallet endpoints – per event
export const getEventWalletBalance = (eventId, userId) =>
  api.get(`/wallet/event/${eventId}/user/${userId}`);export const getMyEventWallets = () => api.get('/wallet/my-wallets');
export const rechargeEventWallet = (data) => api.post('/wallet/recharge', data);

export const getEventWallets = (eventId) => api.get(`/wallet/event/${eventId}`);
export const getEvent = (id) => api.get(`/events/${id}`);
export const getEventTransactions = (eventId, params) => 
  api.get(`/wallet/event/${eventId}/transactions`, { params });
// Add this to your src/services/api.js
export const deleteOrder = (id) => {
  return api.delete(`/orders/${id}`);
};
export const updateMyProfile = (data) =>
  api.put('/auth/profile', data);
 export const getMyProfile = () =>
  api.get('/auth/profile');
export const registerAttendee = (data) =>
  api.post('/auth/attendee-register', data);
// Lookup an existing registered attendee for an event
export const lookupAttendeeForEvent = (eventId, userId) =>
  api.get(`/events/${eventId}/users/lookup/${userId}`);
export const queryKeys = {
  events: ['events'],
  event: (id) => ['events', id],
  eventUsers: (id) => ['events', id, 'users'],
  eventWallets: (id) => ['wallet', id],
  eventTransactions: (id, params) => ['wallet', id, 'transactions', params],
  orders: (eventId) => ['orders', eventId],
  foodItems: (eventId) => ['food-items', eventId],
};

// ============ TICKET TYPES API ============
export const getTicketTypes = (eventId) =>
  api.get(`/events/${eventId}/ticket-types`);

export const createTicketType = (eventId, data) =>
  api.post(`/events/${eventId}/ticket-types`, data);

export const updateTicketType = (eventId, typeId, data) =>
  api.put(`/events/${eventId}/ticket-types/${typeId}`, data);

export const deleteTicketType = (eventId, typeId) =>
  api.delete(`/events/${eventId}/ticket-types/${typeId}`);

export const sendRegistrationOTP = (data) =>
  api.post('/auth/send-otp', data);

export const verifyRegistrationOTP = (data) =>
  api.post('/auth/verify-otp-register', data);



// ============ MEAL COUPONS ============
export const getMealTypes = (eventId) =>
  api.get(`/events/${eventId}/meal-types`);

export const createMealType = (eventId, data) =>
  api.post(`/events/${eventId}/meal-types`, data);

export const updateMealType = (eventId, mealId, data) =>
  api.put(`/events/${eventId}/meal-types/${mealId}`, data);

export const deleteMealType = (eventId, mealId) =>
  api.delete(`/events/${eventId}/meal-types/${mealId}`);

export const assignMeals = (eventId, data) =>
  api.post(`/events/${eventId}/meals/assign`, data);

export const getAttendeeMealStatus = (eventId, attendeeId) =>
  api.get(`/events/${eventId}/meals/status/${attendeeId}`);

export const redeemMeal = (eventId, data) =>
  api.post(`/events/${eventId}/meals/redeem`, data);

export const getMealReport = (eventId) =>
  api.get(`/events/${eventId}/meals/report`);

export const getMealCoupons = (eventId, params) =>
  api.get(`/events/${eventId}/meals/coupons`, { params });
export const getDeviceMealRedemptions = (deviceId, params) =>
  api.get(`/sessions/device-meals/${deviceId}`, { params });

// ============ PUBLIC (unauthenticated) ============
export const getPublicEvent = (eventId) =>
  api.get(`/public/events/${eventId}`);

export const publicEventRegister = (eventId, data) =>
  api.post(`/public/events/${eventId}/register`, data);

export const lookupAttendeeByQR = (eventId, customId) =>
  api.get(`/events/${eventId}/users/lookup-qr/${customId}`);

export const approveEventUser = (eventId, userId, approved) =>
  api.put(
    `/events/${eventId}/users/${userId}/approve`,
    typeof approved === 'boolean' ? { approved } : {}
  );

export const getVendorCollections = (params) =>
  api.get('/wallet/vendor-collections', { params });
export default api;
